// src/features/portfolio-optimization-tools/my-strategies/components/WealthGenerationChart.tsx
//
// The "wealth generation" area chart: it translates the time-weighted index
// into real dollars on the user's initial investment and plots the ABSOLUTE
// dollar gap (strategy minus benchmark). By isolating the wealthDifference,
// the compounding alpha becomes a single widening area — the most persuasive
// read of the strategy's real-money value creation. A dashed vertical marker
// flags where live out-of-sample tracking began.

import { useMemo } from 'react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type { EquityCurvePoint } from '../../multi-asset-portfolio-optimizer/api';
import {
    computeWealthGeneration,
    rebaseToTWRR,
    type WealthPoint,
} from './performanceMath';

const POSITIVE = '#10b981';
const NEGATIVE = '#ef4444';

const formatCurrency = (val: number): string =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    }).format(val);

interface AreaTooltipProps {
    active?: boolean;
    payload?: { payload: WealthPoint }[];
    benchmarkName?: string;
}

const AreaTooltip = ({
    active,
    payload,
    benchmarkName = 'Benchmark',
}: AreaTooltipProps) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const winning = d.wealthDifference >= 0;
    return (
        <div className="bg-slate-900 border border-slate-700 p-4 rounded-xl shadow-2xl min-w-[240px] z-50">
            <div className="flex justify-between items-center border-b border-slate-800 pb-2 mb-3">
                <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">
                    {new Date(d.date).toLocaleDateString()}
                </p>
                {d.isLive && (
                    <span className="text-[9px] font-bold bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 px-2 py-0.5 rounded uppercase tracking-wider">
                        LIVE OOS
                    </span>
                )}
            </div>
            <div className="space-y-2">
                <div className="flex justify-between items-center gap-4">
                    <span className="text-emerald-400 text-xs font-bold">
                        Strategy
                    </span>
                    <span className="text-white font-mono font-bold text-sm">
                        {formatCurrency(d.strategyWealth)}
                    </span>
                </div>
                <div className="flex justify-between items-center gap-4">
                    <span className="text-amber-400 text-xs font-semibold">
                        {benchmarkName}
                    </span>
                    <span className="text-slate-300 font-mono text-xs">
                        {formatCurrency(d.benchmarkWealth)}
                    </span>
                </div>
                <div className="h-px w-full bg-slate-800 my-2" />
                <div className="flex justify-between items-center gap-4 bg-slate-950 p-2 rounded-lg border border-slate-800">
                    <span className="text-slate-300 text-[11px] font-bold uppercase tracking-wider">
                        Alpha Value Created
                    </span>
                    <span
                        className={`font-mono font-black text-sm ${
                            winning ? 'text-emerald-400' : 'text-red-400'
                        }`}
                    >
                        {winning ? '+' : ''}
                        {formatCurrency(d.wealthDifference)}
                    </span>
                </div>
            </div>
        </div>
    );
};

interface WealthGenerationChartProps {
    portfolioCurve: (EquityCurvePoint & { is_out_of_sample?: boolean })[];
    benchmarkCurve: (EquityCurvePoint & { is_out_of_sample?: boolean })[];
    initialInvestment: number;
    benchmarkName?: string;
}

export const WealthGenerationChart = ({
    portfolioCurve,
    benchmarkCurve,
    initialInvestment,
    benchmarkName = 'Benchmark',
}: WealthGenerationChartProps) => {
    const data = useMemo<WealthPoint[]>(
        () =>
            computeWealthGeneration(
                rebaseToTWRR(portfolioCurve),
                rebaseToTWRR(benchmarkCurve),
                initialInvestment,
            ),
        [portfolioCurve, benchmarkCurve, initialInvestment],
    );

    // First date that falls inside the live out-of-sample window, if any.
    const liveStartDate = useMemo(
        () => data.find((d) => d.isLive)?.date ?? null,
        [data],
    );

    // Final dollar gap generated over the full window, for the sub-header.
    const finalGap = data.length > 0 ? data[data.length - 1].wealthDifference : 0;
    const winning = finalGap >= 0;

    if (!portfolioCurve.length || !benchmarkCurve.length || data.length === 0) {
        return null;
    }

    return (
        <div className="w-full">
            <p className="muted text-[11px] mb-3">
                Starting from{' '}
                <span className="font-bold text-slate-300">
                    {formatCurrency(initialInvestment)}
                </span>
                , your strategy has generated{' '}
                <span
                    className={`font-bold ${
                        winning ? 'text-emerald-500' : 'text-red-500'
                    }`}
                >
                    {winning ? '+' : ''}
                    {formatCurrency(finalGap)}
                </span>{' '}
                of additional wealth versus {benchmarkName}. Compounding widens
                this gap over time.
            </p>
            <div className="w-full h-72 text-xs">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={data}
                        margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
                    >
                        <defs>
                            <linearGradient
                                id="wealthGap"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="5%"
                                    stopColor={winning ? POSITIVE : NEGATIVE}
                                    stopOpacity={0.3}
                                />
                                <stop
                                    offset="95%"
                                    stopColor={winning ? POSITIVE : NEGATIVE}
                                    stopOpacity={0}
                                />
                            </linearGradient>
                        </defs>
                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#334155"
                            opacity={0.2}
                        />
                        <XAxis
                            dataKey="date"
                            tickFormatter={(val: string) =>
                                new Date(val).toLocaleDateString(undefined, {
                                    year: '2-digit',
                                    month: 'short',
                                })
                            }
                            minTickGap={50}
                            stroke="#64748b"
                            fontSize={11}
                            tickMargin={10}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            tickFormatter={(val: number) =>
                                `$${(val / 1000).toFixed(0)}k`
                            }
                            stroke="#64748b"
                            fontSize={11}
                            axisLine={false}
                            tickLine={false}
                            width={60}
                        />
                        <Tooltip
                            content={
                                <AreaTooltip benchmarkName={benchmarkName} />
                            }
                            cursor={{ stroke: '#64748b', strokeOpacity: 0.3 }}
                        />
                        <ReferenceLine y={0} stroke="#475569" />
                        {liveStartDate && (
                            <ReferenceLine
                                x={liveStartDate}
                                stroke="#818cf8"
                                strokeDasharray="4 4"
                                label={{
                                    position: 'insideTopRight',
                                    value: 'LIVE ',
                                    fill: '#818cf8',
                                    fontSize: 10,
                                    fontWeight: 'bold',
                                }}
                            />
                        )}
                        <Area
                            type="monotone"
                            dataKey="wealthDifference"
                            stroke={winning ? POSITIVE : NEGATIVE}
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#wealthGap)"
                            name="wealthDifference"
                            isAnimationActive
                            animationDuration={900}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default WealthGenerationChart;
